import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, filter, map, switchMap, take, withLatestFrom } from 'rxjs';

import { PrivaNotificationsService } from '@priva/notifications';

import { selectAppModeIfDefined } from 'app/features/settings/state/settings/settings.selectors';
import { notificationWarningTimeout } from 'app/shared/constants';
import { scanFlowRegistrationDataStorageKey, StorageService } from 'app/shared/services';
import { loadMasterDataSuccess } from 'app/state/master-data/master-data.actions';
import { updatingMultiUserRecentActivities } from 'app/state/multi-user-preferences/multi-user-preferences.actions';
import { addRowToSelection } from 'app/state/row-selection/row-selection.actions';
import { updatingUserRecentActivities } from 'app/state/user-session/user-session.actions';
import { selectUserSessionEmployeeNumber } from 'app/state/user-session/user-session.selectors';

import { isActivitySupportedForScanFlow } from '../../utils/scan-activity.utils';
import { isSystemOrBreakActivity } from '../../utils/time-registration-activity.utils';
import {
    openScanQrDialog,
    processScannedCode,
    resetScannedCode,
} from '../scanned-codes/scanned-codes.actions';
import { selectScannedCodeByToken } from '../scanned-codes/scanned-codes.selectors';
import { createScannedCodeFromActivity } from '../scanned-codes/utils';
import {
    applyScanFlowRegistrationDataFromStorage,
    changeScanFlowRowSelection,
    initScanFlow,
    initScanFlowSuccess,
    resetPartialScanFlowRegistrationData,
    setActivityInScanFlow,
    setScanFlowEmployeeNumber,
    showNotificationScanFlowInProgressRegistrationDataSent,
    startScanCode,
    syncScannedCodeFromStore,
    updateScanFlowRegistrationDataInStorage,
    userInputForScanFlowEnded,
    userInputForScanFlowStarted,
} from './scan-flow.actions';
import {
    mapScanFlowCodesToActivity,
    selectScanFlowAllowedCodeTypes,
    selectScanFlowCodes,
    selectScanFlowRegistrationData,
} from './scan-flow.selectors';
import { getNextActivityAfterRegistration } from './scan-flow.utils';

/**
    Effects for the scan flow feature
    This includes scan flow steps, sync + monitor scan code data and updating data to storage
*/

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class ScanFlowEffects {
    // --- Scan Flow Step ---
    setAndMonitorEmployeeNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initScanFlow, initScanFlowSuccess, applyScanFlowRegistrationDataFromStorage),
            switchMap(() => this.store.select(selectUserSessionEmployeeNumber)),
            filter((employeeNumber): employeeNumber is number => !!employeeNumber),
            withLatestFrom(
                this.store.pipe(selectAppModeIfDefined),
                this.store.pipe(select(selectScanFlowRegistrationData)),
            ),
            map(([userSessionEmployeeNumber, appMode, scanFlowRegistrationData]) => {
                return appMode === 'personal'
                    ? setScanFlowEmployeeNumber({ employeeNumber: userSessionEmployeeNumber })
                    : setScanFlowEmployeeNumber({ employeeNumber: scanFlowRegistrationData.employeeNumber });
            }),
        ),
    );

    // This function is called in response to user clicking on the scan-button
    startBarcodeScan$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startScanCode),
            withLatestFrom(this.store.pipe(selectScanFlowAllowedCodeTypes)),
            switchMap(([, allowedCodeTypes]) => {
                const token = 'scanFlow_' + Date.now();

                return [
                    userInputForScanFlowStarted(),
                    openScanQrDialog({
                        allowedCodeTypes,
                        onScanFinishedAction: userInputForScanFlowEnded(),
                        token,
                    }),
                    syncScannedCodeFromStore({ token }),
                ];
            }),
        ),
    );

    showNotificationScanFlowInProgressRegistrationDataSent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(showNotificationScanFlowInProgressRegistrationDataSent),
                map(() => {
                    const message = 'Previous registration was incomplete, but it was processed anyway';
                    this.notificationService.toaster.warning(message, {
                        behavior: 'transient',
                        timeout: notificationWarningTimeout,
                    });
                    return true;
                }),
            ),
        { dispatch: false },
    );

    // --- Scan Flow Sync and Monitor Data Changes ---
    syncScannedCodeFromStore$ = createEffect(() =>
        this.actions$.pipe(
            ofType(syncScannedCodeFromStore),
            switchMap(({ token }) =>
                this.store.pipe(
                    select(selectScannedCodeByToken(token)),
                    filter((code) => !!code),
                    map((code) => ({ token, code })),
                ),
            ),
            switchMap(({ token, code }) => {
                return [resetScannedCode({ token }), processScannedCode({ scannedText: code })];
            }),
        ),
    );

    syncRowSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addRowToSelection),
            map((rowSelectionItem) => changeScanFlowRowSelection(rowSelectionItem)),
        ),
    );

    monitorActivityEntityType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadMasterDataSuccess),
            withLatestFrom(this.store.pipe(select(selectScanFlowCodes))),
            switchMap(([{ masterData }, scannedCodes]) => {
                return this.store.pipe(
                    mapScanFlowCodesToActivity(scannedCodes),
                    withLatestFrom(this.store.pipe(selectAppModeIfDefined)),
                    map(([registrationDataActivity, appMode]) => {
                        if (appMode === 'multi-user' && registrationDataActivity) {
                            const isTimeRegistrationActivity = isSystemOrBreakActivity(
                                registrationDataActivity.name,
                                registrationDataActivity.number,
                                registrationDataActivity,
                            );
                            if (isTimeRegistrationActivity)
                                return { activity: registrationDataActivity, appMode };
                        }
                        return {
                            activity: masterData.activities.find(
                                (activity) => activity.number === registrationDataActivity?.number,
                            ),
                            appMode,
                        };
                    }),
                    take(1),
                );
            }),
            switchMap(({ activity, appMode }) => {
                if (!activity) return EMPTY;
                if (!isActivitySupportedForScanFlow(activity)) {
                    this.notificationService.toaster.warning('TOASTER.TEXT.ACTIVITY_NO_LONGER_VALID', {
                        behavior: 'transient',
                        timeout: notificationWarningTimeout,
                    });
                    return [resetPartialScanFlowRegistrationData()];
                }

                const newActivity = getNextActivityAfterRegistration({
                    currentActivity: activity,
                    appMode,
                });

                const newActivityCode = newActivity ? createScannedCodeFromActivity(newActivity) : undefined;
                return [
                    setActivityInScanFlow({
                        activityCode: newActivityCode,
                        keepRegistrationData: true,
                    }),
                ];
            }),
        ),
    );

    // --- Scan Flow Storing Data to Local Storage ---
    monitorStateToUpdateLatestScannedCodesInStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initScanFlowSuccess, applyScanFlowRegistrationDataFromStorage),
            switchMap(() => this.store.select(selectScanFlowRegistrationData)),
            map((registrationData) => updateScanFlowRegistrationDataInStorage(registrationData)),
        ),
    );

    storeLastActivityInUserSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setActivityInScanFlow),
            map(({ activityCode }) => activityCode?.number),
            filter((activityNumber): activityNumber is number => !!activityNumber),
            withLatestFrom(this.store.pipe(selectAppModeIfDefined)),
            map(([activityNumber, appMode]) =>
                appMode === 'multi-user'
                    ? updatingMultiUserRecentActivities({ activityNumber })
                    : updatingUserRecentActivities({ activityNumber }),
            ),
        ),
    );

    updateRegistrationDataInStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateScanFlowRegistrationDataInStorage),
                map((registrationData) => {
                    this.storageService.store(scanFlowRegistrationDataStorageKey, registrationData);

                    return true;
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private storageService: StorageService,
        private notificationService: PrivaNotificationsService,
    ) {}
}
